import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Battlefield, AncientTemple, NeonCity, SpaceStation, VolcanicArena } from './stages'
import { FighterSvg, GameContainer, GameHud } from './game'

const GAME_WIDTH = 1200
const GAME_HEIGHT = 600

const stageComponents = {
  neonPier: NeonCity,
  reactorCore: VolcanicArena,
  skySpire: AncientTemple,
  ironRing: SpaceStation,
  battlefield: Battlefield,
}

const stageCamera = {
  neonPier: { x: 0, y: 0 },
  skySpire: { x: 0, y: 0 },
  reactorCore: { x: 0, y: -50 },
  ironRing: { x: 0, y: 0 },
  battlefield: { x: 0, y: -30 },
}

function fighterFromSnapshot(snapshot, slot) {
  const fighter = snapshot?.[slot]
  if (!fighter) return null

  return {
    ...fighter,
    width: fighter.width || 48,
    height: fighter.height || 64,
    slot,
    invuln: fighter.invuln || 0,
    hitstun: fighter.hitstun || 0,
    attackCooldown: fighter.attackCooldown || 0,
    onGround: fighter.onGround ?? true,
  }
}

export function KillCamReplay({ replay, onClose }) {
  const gameContainerRef = useRef(null)
  const [frameIndex, setFrameIndex] = useState(0)
  const frames = replay?.frames || []
  const frame = frames[frameIndex] || frames[0]
  const StageBackground = stageComponents[replay?.stageId || 'battlefield'] || Battlefield

  const gameState = useMemo(() => ({
    left: fighterFromSnapshot(frame, 'p1'),
    right: fighterFromSnapshot(frame, 'p2'),
    running: false,
    winner: replay?.winner || null,
  }), [frame, replay?.winner])

  useEffect(() => {
    if (frames.length <= 1) return undefined

    setFrameIndex(0)
    const interval = window.setInterval(() => {
      setFrameIndex(index => (index + 1 >= frames.length ? 0 : index + 1))
    }, 100)

    return () => window.clearInterval(interval)
  }, [frames.length])

  useEffect(() => {
    const camera = stageCamera[replay?.stageId || 'battlefield'] || stageCamera.battlefield
    gameContainerRef.current?.setCamera(camera.x, camera.y, 1)
  }, [replay?.stageId, frameIndex])

  if (!replay || frames.length === 0) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-6xl rounded-2xl border border-red-500/40 bg-gray-950 p-4 shadow-2xl shadow-red-500/20">
        <div className="mb-3 flex items-center justify-between gap-4">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.35em] text-red-400">Kill Cam</div>
            <div className="text-sm text-gray-400">Final {Math.round((replay.durationMs || 5000) / 1000)} seconds</div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-bold uppercase tracking-wider text-white hover:bg-white/10"
            >
              Close
            </button>
          )}
        </div>

        <GameContainer
          ref={gameContainerRef}
          width={GAME_WIDTH}
          height={GAME_HEIGHT}
          backgroundComponent={StageBackground}
          backgroundProps={{ animate: false }}
          overlayChildren={(
            <>
              <div
                style={{
                  position: 'absolute',
                  top: 64,
                  left: 0,
                  width: GAME_WIDTH,
                  textAlign: 'center',
                  fontSize: 72,
                  fontWeight: 1000,
                  fontStyle: 'italic',
                  letterSpacing: '0.18em',
                  color: 'rgba(248, 113, 113, 0.95)',
                  textShadow: '0 0 20px rgba(239, 68, 68, 0.85), 0 5px 0 rgba(0, 0, 0, 0.7)',
                  pointerEvents: 'none',
                  zIndex: 30,
                }}
              >
                KILL CAM
              </div>
              <GameHud
                gameState={gameState}
                leftCharacter={replay.characters?.p1}
                rightCharacter={replay.characters?.p2}
                gameTime={(frame?.t || 0) / 1000}
                isLocalPlayerLeft={replay.localPlayerSlot !== 'p2'}
              />
            </>
          )}
        >
          {gameState.left && (
            <FighterSvg
              fighter={gameState.left}
              characterId={replay.characters?.p1?.archetype}
              isLocal={replay.localPlayerSlot !== 'p2'}
            />
          )}
          {gameState.right && (
            <FighterSvg
              fighter={gameState.right}
              characterId={replay.characters?.p2?.archetype}
              isLocal={replay.localPlayerSlot === 'p2'}
            />
          )}
        </GameContainer>
      </div>
    </div>
  )
}

export default KillCamReplay
